import {
  AvailablePaidMembershipPlanInGroup
} from '@/models/api/paid-member-price/member-plan/group/available-paid-membership-plan-group'
import {
  CreditcardForSubscriptionRepository,
  FrontApiRepositoryForSubscription,
} from '@/models/payment/creditcard/context/for-subscription/repository'
import { ApiClientWithAuth } from '@/services/api/client'
import {
  CreditcardServiceForSubscription
} from '@/services/api/subscription/payment/creditcard-service-for-subscription'
import { getCreditcardPaymentProvierServiceConfig } from '@/services/payment'
import {
  CreditcardPaymentProviderServiceForSubscription,
} from '@/services/payment/creditcard-payment-provider/creditcard-payment-provider-service'
import { BaseError } from '@/util/base-error'
import { createPaymentParamBuilder } from '@spa/services/order/paymentParamBuilder'
import store from '@spa/store'
import { User } from '@spa/store/modules/user/types'
import { identity } from 'fp-ts/lib/function'
import _ from 'lodash'

export interface ChangePlanParams {
  before: AvailablePaidMembershipPlanInGroup
  after: AvailablePaidMembershipPlanInGroup
}

export interface ChangePaidMembershipPlanRepository {
  upgrade(params: ChangePlanParams): Promise<void>
  downgrade(params: ChangePlanParams): Promise<void>
}

interface CurrentUserGateway {
  getCurrentUser(): Promise<User>

  restoreCache(): Promise<void>
}

export class DefaultChangePaidMembershipPlanRepository implements ChangePaidMembershipPlanRepository {
  static get forHost(): ChangePaidMembershipPlanRepository {
    const cardPaymentProvider = getCreditcardPaymentProvierServiceConfig().forSubscription

    return new DefaultChangePaidMembershipPlanRepository(
      new FrontApiRepositoryForSubscription(
        new CreditcardServiceForSubscription(),
        cardPaymentProvider
      ),
      {
        getCurrentUser: async () => {
          // 取得済みの場合にリクエストを省略するため、storeを経由
          await store.dispatch('user/get')

          const errorOnGet: Error = _.get(store.state.user, 'error.get')
          if (errorOnGet) throw errorOnGet

          const user = store.state.user.user
          if (user === null) throw new FailedGetCurrentUserError()

          return user
        },
        restoreCache: async () => await store.dispatch('user/unload'),
      },
      cardPaymentProvider
    )
  }

  private constructor(
    private readonly creditcardRepository: Pick<CreditcardForSubscriptionRepository, 'findRegistered'>,
    private readonly currentUserGateway: CurrentUserGateway,
    private readonly cardPaymentProvider: CreditcardPaymentProviderServiceForSubscription
  ) {
  }

  downgrade(p: ChangePlanParams): Promise<void> {
    return this.change('downgrade', p)
  }
  upgrade(p: ChangePlanParams): Promise<void> {
    return this.change('upgrade', p)
  }
  private async change(command: 'upgrade' | 'downgrade', p: ChangePlanParams): Promise<void> {
    const [creditcard, user] = await Promise.all([
      this.creditcardRepository.findRegistered().then(result => result.foldL(
        () => { throw new NotFoundCreditcardError() },
        identity
      )),
      this.currentUserGateway.getCurrentUser(),
    ])

    const payment = await this.cardPaymentProvider.createPaymentPayload(
      /**
       * kuronekoが設定値次第ではセキュリティコードを必要とし得るので、 {@see CreditcardPaymentProviderService.createPaymentPayload}の引数は
       * セキュリティコードも必須という型定義になっている(optionalにすると、設定値の組み合わせ次第では動作しなくなる).
       *
       * しかし、 2022/07/26時点では、有料会員費支払いでセキュリティコードを入力する仕様になっていない
       * & kuronekoも決済時にセキュリティコードを必要とする設定は使っていないので、ダミーの空文字を指定している.
       *
       * セキュリティコードを入力するようになったら、以下の対応が必須.
       * - セキュリティコード入力用のフォームを設ける
       * - セキュリティコード用のフィールド変数を定義し、フォームとバインドさせる
       * - 空文字ではなくフィールド変数の値を参照させる
       */
      creditcard.withSecurityCode(''),
      user
    ).then(result => result.fold(
      (e) => {
        throw e
      },
      identity
    ))

    return ApiClientWithAuth.post(`/api/user/paid_membership/plan/${command}`, {
      before_plan: {
        plan_id: p.before.externalId,
        subscribed_id: p.before.subscribeId,
      },
      after_plan: {
        plan_id: p.after.externalId,
      },
      /**
       * 既存の実装が、Engine APIのI/Fに付き合う形でstring前提の実装になっている.
       *
       * Engineの都合はともかく、stirng形式での送信はリクエストのバリデーションが
       * むやみに面倒かつ不正確になるので、少なくともFront内部ではjsonでのやり取りで一貫させたい.
       *
       * {@link https://github.com/my-color/front/issues/6328}の対応時点では、
       * 影響範囲を限定したいので文字列前提のI/Fに一旦付き合いつつ、局所的にJSONへ再変換している.
       */
      payment: JSON.parse(
        createPaymentParamBuilder(payment).build()
      ),
    }).then(() =>
      /**
       * キャッシュ破棄
       * @see https://github.com/my-color/front/issues/6328#issuecomment-1099649686
       */
      this.currentUserGateway.restoreCache()
    )
  }
}

class NotFoundCreditcardError extends BaseError {}
class FailedGetCurrentUserError extends BaseError {}
